import * as Sentry from '@sentry/react';

// Vite dispatches a cancellable `vite:preloadError` when a lazy chunk's CSS or JS
// fails to load. The asset is not missing — nginx falls back to assets.supersimple.io,
// which keeps every build's assets — so these are transient fetch failures, typically
// a connection dropped as pods roll over during a deploy. Nothing listened for the
// event, so a momentary blip threw the user onto the error boundary for good.
//
// A failed stylesheet is recoverable on its own: Vite marks the dep as seen and never
// retries it, but re-appending the link picks the styles up without costing the user
// their session. Cancelling the event lets the chunk itself carry on loading, so at
// worst it renders unstyled for a moment. Anything else needs the page from scratch.

const CssPreloadErrorPattern = /Unable to preload CSS for (.+)$/;
const ReloadTimestampKey = 'supersimple.preload-error-reload-at';
const ReloadCooldownMs = 30_000;

const readReloadTimestamp = () => {
  try {
    return Number(window.sessionStorage.getItem(ReloadTimestampKey));
  } catch {
    return 0;
  }
};

const writeReloadTimestamp = (timestamp: number) => {
  try {
    window.sessionStorage.setItem(ReloadTimestampKey, String(timestamp));
  } catch {
    // Storage can be unavailable in embedded contexts; the reload still helps.
  }
};

// Returns false when a reload just happened, so the caller can report the error
// instead of looping the page on a failure that reloading does not fix.
const reloadOnce = () => {
  const now = Date.now();
  const reloadedAt = readReloadTimestamp();

  if (reloadedAt > 0 && now - reloadedAt < ReloadCooldownMs) {
    return false;
  }

  writeReloadTimestamp(now);
  window.location.reload();
  return true;
};

const retryStylesheet = (href: string, error: Error) => {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.crossOrigin = '';
  link.href = href;
  link.addEventListener('error', () => {
    if (!reloadOnce()) {
      Sentry.captureException(error);
    }
  });
  document.head.appendChild(link);
};

export const handlePreloadError = (event: VitePreloadErrorEvent) => {
  const stylesheet = CssPreloadErrorPattern.exec(event.payload.message)?.at(1);

  if (stylesheet !== undefined) {
    event.preventDefault();
    retryStylesheet(stylesheet, event.payload);
    return;
  }

  if (reloadOnce()) {
    event.preventDefault();
  }
};

export const initPreloadErrorListener = () => {
  window.addEventListener('vite:preloadError', handlePreloadError);
};
